import { Accordion, AccordionDetails, AccordionSummary, Button, Grid } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FormatListNumberedIcon from '@material-ui/icons/FormatListNumbered';
import { PageBreadcrumbDivider } from 'framework/components/breadcrumbs/PageBreadcrumbDivider';
import { PageBreadcrumbTitle } from 'framework/components/breadcrumbs/PageBreadcrumbTitle';
import { SmallPrimaryTextButton } from 'framework/components/buttons/SmallPrimaryTextButton';
import { GridDividerWithCaption } from 'framework/components/detailDialog/GridDividerWithCaption';
import { PropertyInGrid } from 'framework/components/detailDialog/PropertyInGrid';
import { LabelledPropertyX } from 'framework/components/labelledProperty/LabelledPropertyX';
import { PageReloadButtonX } from 'framework/components/layout/PageReloadButtonX';
import { PageXHeaderActions } from 'framework/components/layout/PageXHeaderActions';
import { VatPercentageModelForm } from 'framework/forms/vat-percentage-model.form';
import { useApiEffect } from 'framework/hooks/useApiEffect';
import { useLazyEffect } from 'framework/hooks/useLazyEffect';
import { usePreloadCacheContext } from 'framework/hooks/usePreloadCacheContext';
import { useTrigger } from 'framework/hooks/useTrigger';
import { useAccordionState } from 'framework/utils/useAccordionState';
import {
	IArticlePropertyKey,
	productCatalogSettingsCommand_patchDefaultVatPercentageCharger,
	productCatalogSettingsCommand_patchDefaultVatPercentageHearingAids,
	productCatalogSettingsQuery_settings,
} from 'gen/ApiClient';
import { useCurrentLanguage } from 'localization/useCurrentLanguage';
import { useLocalization } from 'localization/useLocalization';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { useDialogsContext } from 'shared/dialogs/useDialogsContext';
import { RestrictedPageX } from 'shared/layout/RestrictedPageX';
import { ArticlePropertyKeysCacheContext } from './articlePropertyKeys/context/ArticlePropertyKeysCacheContext';
import { localizeApkLabel } from './articlePropertyKeys/utils/localizeApkLabel';
import { ArticlePropertyKeyModelForm } from './forms/articlePropertyDefinitions/ArticlePropertyKeyModelForm';
import { ProductCatalogBreadcrumbs } from './ProductCatalogBreadcrumbs';

type AccordionType = 'article_property_keys' | 'vat_settings';

export const ProductCatalogSettingsPage = () => {
	const strings = useLocalization();
	const [trigger, reload] = useTrigger();
	const [settings, reloadSettings] = useApiEffect(productCatalogSettingsQuery_settings);
	const [expandedAccordion, setExpandedAccordion] = useAccordionState<AccordionType>();
	const { goBack } = useHistory();
	const { open, confirm, cancel } = useDialogsContext(reload);

	useLazyEffect(() => {
		reloadSettings();
	}, [trigger]);

	if (settings === undefined) {
		return <div></div>;
	}

	const onPatchDefaultVatPercentageHearingAids = () => {
		open(
			<VatPercentageModelForm
				cancel={cancel}
				confirm={confirm}
				open
				initialValue={settings.defaultVatPercentageHearingAids as any}
				title={strings.changeWhat(strings.standardVATRateForHearingAids)}
				submitFunction={val => productCatalogSettingsCommand_patchDefaultVatPercentageHearingAids({ vatPercentage: val })}
				formTitle={strings.VATRateForHearingAids}
				label={strings.vatPercentage}
			/>
		);
	};

	const onPatchDefaultVatPercentageChargers = () => {
		open(
			<VatPercentageModelForm
				cancel={cancel}
				confirm={confirm}
				open
				initialValue={settings.defaultVatPercentageCharger as any}
				title={strings.changeWhat(strings.standardVATRateForChargers)}
				submitFunction={val => productCatalogSettingsCommand_patchDefaultVatPercentageCharger({ vatPercentage: val })}
				formTitle={strings.VATRateForChargers}
				label={strings.vatPercentage}
			/>
		);
	};

	return (
		<RestrictedPageX
			pageClaim='ProductCatalog'
			stickyHeader={
				<div
					className='df-col fg1'
					style={{ paddingBottom: 16 }}>
					<div className='df-row-ac'>
						<ProductCatalogBreadcrumbs />
						<PageBreadcrumbDivider />
						<PageBreadcrumbTitle title={strings.settingsAndPreferences} />
						<div className='fg1'></div>
						<PageXHeaderActions>
							<Button
								size='small'
								variant='outlined'
								startIcon={<CloseIcon />}
								onClick={() => goBack()}>
								{strings.closeThisWindow}
							</Button>
							<PageReloadButtonX reload={reload} />
						</PageXHeaderActions>
					</div>
				</div>
			}>
			<div
				className='df-col'
				style={{
					padding: 16,
					maxWidth: 1280,
				}}>
				<Accordion
					expanded={expandedAccordion === 'article_property_keys'}
					onChange={() => setExpandedAccordion('article_property_keys')}>
					<AccordionSummary expandIcon={<ExpandMoreIcon />}>
						<span style={{ fontWeight: 500 }}>{strings.productProperties}</span>
					</AccordionSummary>
					<AccordionDetails>
						<ArticlePropertyKeysStack reload={reload} />
					</AccordionDetails>
				</Accordion>
				<Accordion
					expanded={expandedAccordion === 'vat_settings'}
					onChange={() => setExpandedAccordion('vat_settings')}>
					<AccordionSummary expandIcon={<ExpandMoreIcon />}>
						<span style={{ fontWeight: 500 }}>{strings.standardVATRates}</span>
					</AccordionSummary>
					<AccordionDetails>
						<div className='df-col gap-4'>
							<LabelledPropertyX
								icon={<FormatListNumberedIcon />}
								label={strings.standardVATRateForHearingAids}
								edit={onPatchDefaultVatPercentageHearingAids}>
								{`${settings.defaultVatPercentageHearingAids} %`}
							</LabelledPropertyX>
							<LabelledPropertyX
								icon={<FormatListNumberedIcon />}
								label={strings.standardVATRateForChargers}
								edit={onPatchDefaultVatPercentageChargers}>
								{`${settings.defaultVatPercentageCharger} %`}
							</LabelledPropertyX>
						</div>
					</AccordionDetails>
				</Accordion>
			</div>
		</RestrictedPageX>
	);
};

type ArticlePropertyKeysStackProps = {
	reload: VoidFunction;
};

const ArticlePropertyKeysStack = ({ reload }: ArticlePropertyKeysStackProps) => {
	const [data] = usePreloadCacheContext(ArticlePropertyKeysCacheContext);
	const language = useCurrentLanguage();
	const strings = useLocalization();
	const { open, confirm, cancel } = useDialogsContext(reload);

	const onNew = () => {
		open(
			<ArticlePropertyKeyModelForm
				open
				cancel={cancel}
				confirm={confirm}
			/>
		);
	};

	const onEdit = (apk: IArticlePropertyKey) => {
		open(
			<ArticlePropertyKeyModelForm
				open
				cancel={cancel}
				confirm={confirm}
				item={apk}
			/>
		);
	};

	return (
		<Grid
			container
			spacing={2}>
			<GridDividerWithCaption
				caption={strings.fieldsToFill}
				action={
					<SmallPrimaryTextButton
						startIcon={<AddIcon />}
						onClick={onNew}>
						{strings.new}
					</SmallPrimaryTextButton>
				}
			/>
			{data.map(t => (
				<PropertyInGrid
					key={t.key}
					labelGridSize={5}
					label={`${localizeApkLabel(t, language)} [${t.key}]`}
					value={t.isUserDefined ? strings.userDefined : strings.predefined}
					edit={t.isUserDefined ? () => onEdit(t) : undefined}
				/>
			))}
		</Grid>
	);
};
